import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import Plot from 'react-plotly.js';
import axios from 'axios';

const DEFAULT_B27_SENSORS = Array.from({ length: 32 }, (_, i) => `Strain(${i + 1})`);

const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location.port === '3000') {
    return `http://${window.location.hostname}:3001/api`;
  }
  return '/api';
};

const apiUrl = (path) => `${getApiBase()}${path.startsWith('/') ? path : `/${path}`}`;

const axiosGetWithRetry = async (url, config, retries = 4) => {
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      return await axios.get(url, { ...config, withCredentials: false });
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      if (status !== 502 && status !== 503 && status !== 504) {
        throw err;
      }
      await new Promise((r) => { setTimeout(r, 1500 * (attempt + 1)); });
    }
  }
  throw lastErr;
};

const buildAnomalyPointTraces = (timestamps, yValues, points, name) => {
  const tsIndex = new Map((timestamps || []).map((t, i) => [t, i]));
  const xs = [];
  const ys = [];
  (points || []).forEach((p) => {
    if (!p.flag) {
      return;
    }
    const i = tsIndex.get(p.timestamp);
    const y = i != null ? yValues[i] : null;
    if (y != null) {
      xs.push(p.timestamp);
      ys.push(y);
    }
  });
  if (xs.length === 0) {
    return [];
  }
  return [{
    x: xs,
    y: ys,
    type: 'scatter',
    mode: 'markers',
    name,
    marker: { color: '#d62728', size: 7, symbol: 'x' },
  }];
};

const schemeGraphLabel = (sensorData) => {
  const months = sensorData?.train_months ?? 2;
  const initialDays = sensorData?.initial_train_data_days ?? (months * 30);
  const testDays = sensorData?.test_days ?? 15;
  return `${months}-month train scheme (${initialDays} data-days train + ${testDays} test)`;
};

const buildMseTraces = (block, labelPrefix, color) => {
  if (!block?.mse_scores?.length) {
    return [];
  }
  const thr = block.threshold ?? block.initial_threshold;
  const lineColor = color
    || (labelPrefix.startsWith('LSTM') ? '#2ca02c' : '#1f77b4');
  const thrColor = color
    ? undefined
    : (labelPrefix.startsWith('LSTM') ? '#98df8a' : '#ff7f0e');
  return [
    {
      x: block.timestamps,
      y: block.mse_scores,
      type: 'scatter',
      mode: 'lines',
      name: `${labelPrefix} MSE`,
      line: { color: lineColor, width: 1.5 },
    },
    {
      x: block.timestamps,
      y: block.mse_scores.map(() => thr),
      type: 'scatter',
      mode: 'lines',
      name: `${labelPrefix} threshold`,
      line: {
        color: thrColor || lineColor,
        width: 2,
        dash: 'dash',
      },
    },
  ];
};

const LSTM_SEQ_COLORS = {
  6: '#ff7f0e',
  12: '#9467bd',
  48: '#d62728',
};

const DEFAULT_LSTM_SEQS = [6, 12, 48];

const DEFAULT_SOURCES = [
  { id: 'b27', label: 'B27 — training folder (B27.csv)' },
  { id: 'dataaaa1', label: 'dataaaa1 — train 2 folder (dataaaa1.csv)' },
  { id: 'm16', label: 'M16 — endpier1 folder (M16.csv)' },
];

const REGIME_SOURCE_IDS = new Set(['b27', 'dataaaa1', 'm16']);
const MUX_SOURCE_IDS = new Set(['b27', 'm16']);

const DEFAULT_WINDOW_SCHEMES = [
  {
    id: '2mo_15d',
    label: '2 months train + 15 days test (slide: drop oldest 15 when adding next 15)',
    train_months: 2,
    test_days: 15,
  },
  {
    id: '3mo_20d',
    label: '3 months train + 20 days test (expand train after each test)',
    train_months: 3,
    test_days: 20,
  },
  {
    id: '1mo_1d',
    label: '1 month train + 1 day test (expand train after each test)',
    train_months: 1,
    test_days: 1,
  },
];

const schemeShortLabel = (scheme) => {
  if (scheme?.id === '1mo_1d' || scheme?.id === '1mo_15d' || scheme?.id === '1mo_5d_slide1d') {
    return '1mo+1d';
  }
  if (scheme?.id === '2mo_15d' || scheme?.id === '2mo_10d') return '2mo+15d';
  if (scheme?.id === '3mo_20d') return '3mo+20d';
  return scheme?.label || scheme?.id || 'rolling';
};

const formatProcessingTime = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return rem > 0 ? `${m} min ${rem} s` : `${m} min`;
};

const B27RegimeRolling = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [sourceList, setSourceList] = useState(DEFAULT_SOURCES);
  const [selectedSource, setSelectedSource] = useState('b27');
  const [schemeList, setSchemeList] = useState(DEFAULT_WINDOW_SCHEMES);
  const [selectedWindowScheme, setSelectedWindowScheme] = useState('2mo_15d');
  const [sensorList, setSensorList] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState('Strain(1)');
  const [selectedWindow, setSelectedWindow] = useState(0);
  const [includeZeroed, setIncludeZeroed] = useState(false);
  const [jobStatus, setJobStatus] = useState('');
  const [lastProcessingSeconds, setLastProcessingSeconds] = useState(null);

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const res = await axiosGetWithRetry(apiUrl('/analysis-files'), { timeout: 30000 });
        const files = (res.data?.files || []).filter((f) => REGIME_SOURCE_IDS.has(f.id));
        if (files.length > 0) {
          setSourceList(files);
        }
      } catch {
        /* keep DEFAULT_SOURCES */
      }
    };
    fetchSources();
  }, []);

  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const res = await axiosGetWithRetry(apiUrl('/b27-regime-rolling/schemes'), { timeout: 30000 });
        const schemes = res.data?.schemes || [];
        if (schemes.length > 0) {
          setSchemeList(schemes);
        }
      } catch {
        /* keep DEFAULT_WINDOW_SCHEMES */
      }
    };
    fetchSchemes();
  }, []);

  useEffect(() => {
    const fetchSensors = async () => {
      try {
        const res = await axiosGetWithRetry(apiUrl('/b27-regime-sensors'), {
          params: { source: selectedSource },
          timeout: 120000,
        });
        const sensors = res.data?.sensors || [];
        if (sensors.length > 0) {
          setSensorList(sensors);
          setSelectedSensor((prev) => (sensors.includes(prev) ? prev : sensors[0]));
        } else {
          setSensorList(MUX_SOURCE_IDS.has(selectedSource) ? DEFAULT_B27_SENSORS : []);
          setSelectedSensor('Strain(1)');
        }
      } catch {
        setSensorList(MUX_SOURCE_IDS.has(selectedSource) ? DEFAULT_B27_SENSORS : []);
      }
    };
    fetchSensors();
    setResults(null);
  }, [selectedSource]);

  useEffect(() => {
    setResults(null);
  }, [selectedWindowScheme]);

  const activeSchemeMeta = schemeList.find((s) => s.id === selectedWindowScheme)
    || DEFAULT_WINDOW_SCHEMES.find((s) => s.id === selectedWindowScheme);

  const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

  const loadData = async () => {
    if (!selectedSensor) {
      setError('Select a sensor first.');
      return;
    }
    setLoading(true);
    setError(null);
    setLastProcessingSeconds(null);
    setJobStatus('Starting rolling walk-forward…');
    try {
      const startRes = await axiosGetWithRetry(apiUrl('/b27-regime-rolling/start'), {
        params: {
          source: selectedSource,
          sequence_length: 5,
          sensor: selectedSensor,
          window_scheme: selectedWindowScheme,
          include_zeroed: includeZeroed ? 1 : 0,
        },
        timeout: 120000,
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        setError('Server did not return a job id.');
        return;
      }

      for (let attempt = 0; attempt < 900; attempt += 1) {
        const statusRes = await axiosGetWithRetry(
          apiUrl(`/b27-regime-rolling/status/${jobId}`),
          { timeout: 120000 },
          2,
        );
        const { status } = statusRes.data;
        if (status === 'pending' || status === 'running') {
          const elapsed = statusRes.data.elapsed_seconds;
          const elapsedLabel = elapsed != null ? formatProcessingTime(elapsed) : `${attempt}s`;
          setJobStatus(
            `Rolling ${schemeShortLabel(activeSchemeMeta)} for ${selectedSensor}… (${elapsedLabel})`,
          );
          await sleep(1000);
          continue;
        }
        if (status === 'complete') {
          const payload = statusRes.data.result;
          if (payload?.error) {
            setError(payload.error);
            return;
          }
          const procSec = statusRes.data.processing_seconds
            ?? payload?.processing_seconds
            ?? payload?.sensors?.[0]?.processing_seconds;
          setLastProcessingSeconds(procSec ?? null);
          setResults(payload);
          setSelectedWindow(0);
          setJobStatus('');
          return;
        }
        if (status === 'error') {
          setError(statusRes.data.error || 'Rolling analysis failed.');
          return;
        }
        setError(`Unexpected job status: ${status}`);
        return;
      }
      setError('Job still running after 15 minutes. Try again later.');
    } catch (err) {
      if (err.response?.status === 504) {
        const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        setError(`Gateway timeout. Use http://${host}:3001/b27-regime-rolling`);
      } else if (err.response) {
        setError(err.response.data?.error || `Server error: ${err.response.status}`);
      } else {
        setError(err.message || 'Failed to load rolling analysis.');
      }
    } finally {
      setLoading(false);
      setJobStatus('');
    }
  };

  const sensorData = (results?.sensors || []).find((s) => s.status === 'ok')
    || (results?.sensors || [])[0]
    || null;

  const fullTimeline = sensorData?.full_timeline || {};
  const fullTimelineZeroed = sensorData?.full_timeline_zeroed || {};
  const allFfPoints = (sensorData?.windows || []).flatMap(
    (w) => (w.models?.feedforward?.points || []),
  );
  const allLstmPoints = (sensorData?.windows || []).flatMap(
    (w) => (w.models?.lstm?.points || []),
  );
  const lstmSeqLengths = sensorData?.lstm_sequence_lengths?.length
    ? sensorData.lstm_sequence_lengths
    : DEFAULT_LSTM_SEQS;
  const allLstmPointsBySeq = {};
  lstmSeqLengths.forEach((seq) => {
    const key = String(seq);
    allLstmPointsBySeq[key] = (sensorData?.windows || []).flatMap(
      (w) => (w.models?.lstm_by_seq?.[key]?.points || []),
    );
  });
  const allFfZeroedPoints = (sensorData?.windows || []).flatMap(
    (w) => (w.models_zeroed?.feedforward?.points || []),
  );
  const allLstmZeroedPoints = (sensorData?.windows || []).flatMap(
    (w) => (w.models_zeroed?.lstm?.points || []),
  );
  const okWindows = (sensorData?.windows || []).filter((w) => w.status === 'ok');
  const activeWindow = okWindows.find((w) => w.window_index === selectedWindow)
    || okWindows[0]
    || null;

  const includeZeroedResult = results?.include_zeroed ?? sensorData?.include_zeroed;
  const displaySensor = sensorData?.sensor || results?.sensor || selectedSensor;
  const processingSeconds = lastProcessingSeconds
    ?? results?.processing_seconds
    ?? sensorData?.processing_seconds;
  const strainPlot = sensorData?.status === 'ok' ? [{
    x: fullTimeline.timestamps,
    y: fullTimeline.strain_raw,
    type: 'scatter',
    mode: 'lines',
    name: `${displaySensor} — original strain`,
    line: { color: '#2ca02c', width: 1.5 },
  }] : [];

  const ffAnomalyTraces = sensorData?.status === 'ok'
    ? buildAnomalyPointTraces(
      fullTimeline.timestamps,
      fullTimeline.strain_raw,
      allFfPoints,
      'FF anomaly (test)',
    )
    : [];

  const lstmAnomalyTraces = sensorData?.status === 'ok'
    ? buildAnomalyPointTraces(
      fullTimeline.timestamps,
      fullTimeline.strain_raw,
      allLstmPoints,
      'LSTM anomaly (test)',
    )
    : [];

  const ffReconstructedPlot = sensorData?.status === 'ok' && fullTimeline.reconstructed_strain?.length
    ? [{
      x: fullTimeline.reconstructed_strain_timestamps || fullTimeline.timestamps,
      y: fullTimeline.reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: 'FF reconstructed strain',
      line: { color: '#1f77b4', width: 1.5, dash: 'dash' },
    }]
    : [];

  const lstmReconstructedPlot = sensorData?.status === 'ok' && fullTimeline.reconstructed_strain_lstm?.length
    ? [{
      x: fullTimeline.reconstructed_strain_lstm_timestamps || fullTimeline.timestamps,
      y: fullTimeline.reconstructed_strain_lstm,
      type: 'scatter',
      mode: 'lines',
      name: 'LSTM reconstructed strain (seq=12)',
      line: { color: '#9467bd', width: 1.5, dash: 'dot' },
    }]
    : [];

  const lstmReconBySeqPlots = {};
  lstmSeqLengths.forEach((seq) => {
    const key = String(seq);
    const block = fullTimeline.reconstructed_strain_lstm_by_seq?.[key];
    if (!block?.reconstructed_strain?.length) {
      return;
    }
    lstmReconBySeqPlots[key] = [{
      x: block.timestamps || fullTimeline.timestamps,
      y: block.reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: `LSTM recon seq=${seq}`,
      line: { color: LSTM_SEQ_COLORS[seq] || '#9467bd', width: 1.5, dash: 'dot' },
    }];
  });

  const lstmAnomalyTracesBySeq = {};
  lstmSeqLengths.forEach((seq) => {
    const key = String(seq);
    lstmAnomalyTracesBySeq[key] = sensorData?.status === 'ok'
      ? buildAnomalyPointTraces(
        fullTimeline.timestamps,
        fullTimeline.strain_raw,
        allLstmPointsBySeq[key],
        `LSTM seq=${seq} anomaly`,
      )
      : [];
  });

  const zeroedStrainPlot = sensorData?.status === 'ok' && fullTimelineZeroed?.timestamps?.length
    ? [{
      x: fullTimelineZeroed.timestamps,
      y: fullTimelineZeroed.strain_zeroed,
      type: 'scatter',
      mode: 'lines',
      name: `${displaySensor} — zeroed strain`,
      line: { color: '#9467bd', width: 1.5 },
    }]
    : [];

  const ffZeroedAnomalyTraces = sensorData?.status === 'ok'
    ? buildAnomalyPointTraces(
      fullTimelineZeroed.timestamps,
      fullTimelineZeroed.strain_zeroed,
      allFfZeroedPoints,
      'FF anomaly (zeroed train)',
    )
    : [];

  const lstmZeroedAnomalyTraces = sensorData?.status === 'ok'
    ? buildAnomalyPointTraces(
      fullTimelineZeroed.timestamps,
      fullTimelineZeroed.strain_zeroed,
      allLstmZeroedPoints,
      'LSTM anomaly (zeroed train)',
    )
    : [];

  const windowFfMsePlot = activeWindow?.models?.feedforward
    ? buildMseTraces(activeWindow.models.feedforward, 'Feedforward')
    : [];

  const windowLstmMseBySeq = {};
  lstmSeqLengths.forEach((seq) => {
    const key = String(seq);
    const block = activeWindow?.models?.lstm_by_seq?.[key]
      || (Number(seq) === 12 ? activeWindow?.models?.lstm : null);
    windowLstmMseBySeq[key] = block
      ? buildMseTraces(block, `LSTM seq=${seq}`, LSTM_SEQ_COLORS[seq])
      : [];
  });

  const lstmAnomalyBySeqSummary = sensorData?.lstm_anomaly_count_by_seq || {};

  const sensorOptions = sensorList.length > 0 ? sensorList : DEFAULT_B27_SENSORS;
  const resultTrainMonths = sensorData?.train_months ?? activeSchemeMeta?.train_months ?? 2;
  const resultTestDays = sensorData?.test_days ?? activeSchemeMeta?.test_days ?? 15;
  const finetuneTestDays = resultTestDays;
  const finetuneEpochs = sensorData?.rolling_training?.finetune_epochs ?? 30;
  const initialEpochs = sensorData?.rolling_training?.initial_feedforward_epochs
    ?? sensorData?.rolling_training?.initial_lstm_epochs
    ?? 80;

  return (
    <Box sx={{ p: 3, pt: '80px', maxWidth: 1400, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          B27 Rolling Autoencoder (walk-forward)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Walk-forward with feedforward + LSTM autoencoders. Window 0:
          {' '}
          {initialEpochs}
          {' '}
          epochs on the
          initial block; each later window warm-starts and fine-tunes
          {' '}
          {finetuneEpochs}
          {' '}
          epochs on the
          last
          {' '}
          {finetuneTestDays}
          {' '}
          data-day
          {finetuneTestDays === 1 ? '' : 's'}
          {' '}
          only.
          <strong> 1 month = 30 days with valid readings</strong>
          {' '}
          (gap days skipped).
          <strong>
            {' '}
            {resultTestDays}
            -day test
          </strong>
          {' '}
          =
          {' '}
          {resultTestDays}
          {' '}
          data-present day
          {resultTestDays === 1 ? '' : 's'}
          .
          For 2mo+15d, train stays ~60 data-days: after each test, drop the oldest 15 days
          and add the newly tested 15. Expand schemes keep growing train. LSTM decoder is
          seeded with the encoder final state and uses a positional signal per reconstruction step.
          LSTM threshold uses held-out validation MSE (last ~15–20% of the train window, or
          the previous 15-day test when warm-starting) — not in-sample train MSE.
          {' '}
          <Link to="/b27-regime-adaptation">80/20 split page</Link>
          {' '}
          is unchanged.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <FormControl sx={{ minWidth: 280 }} size="small" required>
            <InputLabel>Data file</InputLabel>
            <Select
              label="Data file"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
            >
              {sourceList.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label || f.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 320 }} size="small" required>
            <InputLabel>Window scheme</InputLabel>
            <Select
              label="Window scheme"
              value={selectedWindowScheme}
              onChange={(e) => setSelectedWindowScheme(e.target.value)}
            >
              {schemeList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label || s.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }} size="small" required>
            <InputLabel>Sensor</InputLabel>
            <Select
              label="Sensor"
              value={selectedSensor}
              onChange={(e) => setSelectedSensor(e.target.value)}
            >
              {sensorOptions.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadData} disabled={loading || !selectedSensor}>
            {loading ? <CircularProgress size={24} /> : `Run ${selectedSensor}`}
          </Button>
          <FormControlLabel
            control={(
              <Checkbox
                checked={includeZeroed}
                onChange={(e) => setIncludeZeroed(e.target.checked)}
                disabled={loading}
              />
            )}
            label="Also train on zeroed strain (slower)"
          />
        </Box>

        {loading && (
          <Alert severity="info" sx={{ mb: 2 }}>{jobStatus || 'Running…'}</Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      </Paper>

      {sensorData?.status === 'ok' && (
        <>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Summary</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {sensorData.scheme_label || sensorData.scheme}
              {' | '}
              {sensorData.calendar_month_range?.start}
              {' → '}
              {sensorData.calendar_month_range?.end}
              {' | '}
              {sensorData.windows_completed}
              /
              {sensorData.window_count}
              {' '}
              windows completed
              {' | '}
              Processing time:
              {' '}
              <strong>{formatProcessingTime(processingSeconds)}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Active data-days (valid readings):
              {' '}
              <strong>{sensorData.active_data_day_count ?? '—'}</strong>
              {' | '}
              Gap days (no valid data):
              {' '}
              <strong>{sensorData.gap_day_count ?? 0}</strong>
              {' | '}
              FF anomalies:
              {' '}
              <strong>{sensorData.feedforward_anomaly_count ?? 0}</strong>
              {' | '}
              LSTM anomalies (seq=12):
              {' '}
              <strong>{sensorData.lstm_anomaly_count ?? 0}</strong>
              {lstmSeqLengths.map((seq) => (
                <span key={seq}>
                  {' | '}
                  LSTM seq=
                  {seq}
                  :
                  {' '}
                  <strong>{lstmAnomalyBySeqSummary[String(seq)] ?? 0}</strong>
                </span>
              ))}
              {includeZeroedResult && (
                <>
                  {' | '}
                  FF anomalies (zeroed train):
                  {' '}
                  <strong>{sensorData.feedforward_anomaly_count_zeroed ?? 0}</strong>
                  {' | '}
                  LSTM anomalies (zeroed train):
                  {' '}
                  <strong>{sensorData.lstm_anomaly_count_zeroed ?? 0}</strong>
                </>
              )}
            </Typography>
            {sensorData.window_basis_note && (
              <Typography variant="body2" color="text.secondary">
                {sensorData.window_basis_note}
              </Typography>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {displaySensor}
              {' '}
              — original strain (full timeline)
            </Typography>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
              Sensor:
              {' '}
              <strong>{displaySensor}</strong>
              {' | '}
              Scheme:
              {' '}
              {sensorData.scheme_train_label || schemeGraphLabel(sensorData)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Green = original strain; blue dashed = FF reconstruction; purple dotted = LSTM
              reconstruction (seq=12, final model). Red × = MSE above threshold.
              Separate LSTM seq=6 / 12 / 48 graphs are below.
            </Typography>
            <Plot
              data={[
                ...strainPlot,
                ...ffReconstructedPlot,
                ...lstmReconstructedPlot,
                ...ffAnomalyTraces,
                ...lstmAnomalyTraces,
              ]}
              layout={{
                title: {
                  text: `${displaySensor} — ${schemeGraphLabel(sensorData)}`,
                  font: { size: 13 },
                },
                xaxis: { title: 'Timestamp' },
                yaxis: { title: 'Strain' },
                height: 440,
                hovermode: 'x unified',
                legend: { orientation: 'h', y: 1.14 },
              }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </Paper>

          {lstmSeqLengths.map((seq) => {
            const key = String(seq);
            const recon = lstmReconBySeqPlots[key] || [];
            const anomalies = lstmAnomalyTracesBySeq[key] || [];
            return (
              <Paper elevation={2} sx={{ p: 2, mb: 3 }} key={`lstm-seq-${seq}`}>
                <Typography variant="h6" gutterBottom>
                  {displaySensor}
                  {' '}
                  — LSTM sequence length
                  {' '}
                  {seq}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Original strain with LSTM reconstruction and anomalies for seq=
                  {seq}
                  .
                  Anomalies (red ×):
                  {' '}
                  <strong>{lstmAnomalyBySeqSummary[key] ?? 0}</strong>
                </Typography>
                <Plot
                  data={[...strainPlot, ...recon, ...anomalies]}
                  layout={{
                    title: {
                      text: `${displaySensor} — LSTM seq=${seq} (${schemeGraphLabel(sensorData)})`,
                      font: { size: 13 },
                    },
                    xaxis: { title: 'Timestamp' },
                    yaxis: { title: 'Strain' },
                    height: 400,
                    hovermode: 'x unified',
                    legend: { orientation: 'h', y: 1.14 },
                  }}
                  style={{ width: '100%' }}
                  useResizeHandler
                />
              </Paper>
            );
          })}

          {includeZeroedResult && zeroedStrainPlot.length > 0 && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {displaySensor}
                {' '}
                — zeroed strain (full timeline)
              </Typography>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                Baseline (first reading):
                {' '}
                <strong>{sensorData.baseline?.toFixed(4) ?? '—'}</strong>
                {' | '}
                Strain zeroed = raw − baseline; models retrained on zeroed values
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Red × = anomalies from feedforward/LSTM trained on zeroed strain (separate from
                the original-strain chart above).
              </Typography>
              <Plot
                data={[...zeroedStrainPlot, ...ffZeroedAnomalyTraces, ...lstmZeroedAnomalyTraces]}
                layout={{
                  title: {
                    text: `${displaySensor} — zeroed strain (${schemeGraphLabel(sensorData)})`,
                    font: { size: 13 },
                  },
                  xaxis: { title: 'Timestamp' },
                  yaxis: { title: 'Zeroed strain' },
                  height: 440,
                  hovermode: 'x unified',
                  legend: { orientation: 'h', y: 1.14 },
                }}
                style={{ width: '100%' }}
                useResizeHandler
              />
            </Paper>
          )}

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Walk-forward windows</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Train</TableCell>
                  <TableCell>
                    Test (
                    {resultTestDays}
                    {' '}
                    data-days)
                  </TableCell>
                  <TableCell>Test range</TableCell>
                  <TableCell align="right">FF anomalies</TableCell>
                  <TableCell align="right">LSTM seq6</TableCell>
                  <TableCell align="right">LSTM seq12</TableCell>
                  <TableCell align="right">LSTM seq48</TableCell>
                  <TableCell align="right">FF threshold</TableCell>
                  <TableCell align="right">LSTM thr (12)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sensorData.windows || []).map((w) => (
                  <TableRow
                    key={w.window_index}
                    selected={w.status === 'ok' && w.window_index === activeWindow?.window_index}
                  >
                    <TableCell>{w.window_index}</TableCell>
                    <TableCell>{w.train_label || w.train_period?.label || '—'}</TableCell>
                    <TableCell>{w.test_label || w.test_period?.label || '—'}</TableCell>
                    <TableCell>
                      {w.status === 'ok'
                        ? `${w.test_period?.start || ''} → ${w.test_period?.end || ''}`
                        : w.reason || w.status}
                    </TableCell>
                    <TableCell align="right">{w.anomaly_count ?? '—'}</TableCell>
                    <TableCell align="right">
                      {w.lstm_anomaly_counts?.['6'] ?? '—'}
                    </TableCell>
                    <TableCell align="right">
                      {w.lstm_anomaly_counts?.['12'] ?? w.lstm_anomaly_count ?? '—'}
                    </TableCell>
                    <TableCell align="right">
                      {w.lstm_anomaly_counts?.['48'] ?? '—'}
                    </TableCell>
                    <TableCell align="right">
                      {w.initial_threshold != null ? w.initial_threshold.toExponential(2) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {w.lstm_initial_threshold != null
                        ? w.lstm_initial_threshold.toExponential(2)
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {okWindows.length > 0 && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Test window MSE detail</Typography>
                <FormControl sx={{ minWidth: 280 }} size="small">
                  <InputLabel>Window</InputLabel>
                  <Select
                    label="Window"
                    value={activeWindow?.window_index ?? 0}
                    onChange={(e) => setSelectedWindow(Number(e.target.value))}
                  >
                    {okWindows.map((w) => (
                      <MenuItem key={w.window_index} value={w.window_index}>
                        #
                        {w.window_index}
                        {' '}
                        test
                        {' '}
                        {w.test_label || w.test_month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {activeWindow && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Train:
                  {' '}
                  {activeWindow.train_period?.start}
                  {' → '}
                  {activeWindow.train_period?.end}
                  {' | '}
                  Test:
                  {' '}
                  {activeWindow.test_period?.start}
                  {' → '}
                  {activeWindow.test_period?.end}
                  {' | '}
                  FF threshold:
                  {' '}
                  {activeWindow.initial_threshold?.toExponential(3) ?? '—'}
                  {' | '}
                  LSTM threshold:
                  {' '}
                  {activeWindow.lstm_initial_threshold?.toExponential(3) ?? '—'}
                </Typography>
              )}
              {activeWindow?.lstm_seq_comparison?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    LSTM seq comparison (fit val MSE → test MSE)
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>seq</TableCell>
                        <TableCell align="right">mean_mse</TableCell>
                        <TableCell align="right">std_mse</TableCell>
                        <TableCell align="right">threshold</TableCell>
                        <TableCell align="right">mean_test_mse</TableCell>
                        <TableCell align="right">max_test_mse</TableCell>
                        <TableCell align="right">anomaly_count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeWindow.lstm_seq_comparison.map((row) => (
                        <TableRow key={row.sequence_length}>
                          <TableCell>{row.sequence_length}</TableCell>
                          <TableCell align="right">
                            {row.mean_mse != null ? Number(row.mean_mse).toExponential(3) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {row.std_mse != null ? Number(row.std_mse).toExponential(3) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {row.threshold != null ? Number(row.threshold).toExponential(3) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {row.mean_test_mse != null ? Number(row.mean_test_mse).toExponential(3) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {row.max_test_mse != null ? Number(row.max_test_mse).toExponential(3) : '—'}
                          </TableCell>
                          <TableCell align="right">{row.anomaly_count ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              <Plot
                data={[...windowFfMsePlot]}
                layout={{
                  title: { text: 'Feedforward MSE (selected window)', font: { size: 13 } },
                  xaxis: { title: 'Timestamp' },
                  yaxis: { title: 'MSE' },
                  height: 360,
                  hovermode: 'x unified',
                  legend: { orientation: 'h', y: 1.15 },
                }}
                style={{ width: '100%', marginBottom: 24 }}
                useResizeHandler
              />
              {lstmSeqLengths.map((seq) => {
                const key = String(seq);
                const traces = windowLstmMseBySeq[key] || [];
                if (traces.length === 0) {
                  return (
                    <Alert severity="info" sx={{ mb: 2 }} key={`mse-empty-${seq}`}>
                      No LSTM MSE for seq=
                      {seq}
                      {' '}
                      on this window.
                    </Alert>
                  );
                }
                return (
                  <Box key={`mse-lstm-${seq}`} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      LSTM MSE — sequence length
                      {' '}
                      {seq}
                    </Typography>
                    <Plot
                      data={traces}
                      layout={{
                        title: {
                          text: `LSTM seq=${seq} MSE (window #${activeWindow?.window_index ?? 0})`,
                          font: { size: 13 },
                        },
                        xaxis: { title: 'Timestamp' },
                        yaxis: { title: 'MSE' },
                        height: 360,
                        hovermode: 'x unified',
                        legend: { orientation: 'h', y: 1.15 },
                      }}
                      style={{ width: '100%' }}
                      useResizeHandler
                    />
                  </Box>
                );
              })}
            </Paper>
          )}
        </>
      )}

      {results && sensorData?.status !== 'ok' && (
        <Alert severity="warning">
          {sensorData?.reason || 'Rolling analysis did not complete.'}
        </Alert>
      )}
    </Box>
  );
};

export default B27RegimeRolling;
